import { useEffect, useState } from "react";
import { MessageSquareWarning, User, Send, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/brands";

const inputCls =
  "w-full border-2 border-hair bg-paper px-4 py-3 font-body text-sm text-ink placeholder:text-muted/70 transition-colors focus:border-accent focus:outline-none";

const VOTED_KEY = "genie_voted_issues";

function getVoted() {
  try {
    return new Set(JSON.parse(localStorage.getItem(VOTED_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

function shortDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}

export default function BrandIssues({ brand }) {
  const [issues, setIssues] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", issue: "" });
  const [submitting, setSubmitting] = useState(false);
  const [voted, setVoted] = useState(getVoted());

  useEffect(() => {
    let active = true;
    api
      .get(`/brands/${brand.slug}/issues`)
      .then(({ data }) => active && setIssues(data))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [brand.slug]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  function resort(list) {
    return [...list].sort((a, b) => (b.votes || 0) - (a.votes || 0) || (b.created_at || "").localeCompare(a.created_at || ""));
  }

  async function upvote(id) {
    if (voted.has(id)) {
      toast.info("You've already upvoted this");
      return;
    }
    try {
      const { data } = await api.post(`/brands/${brand.slug}/issues/${id}/vote`);
      setIssues((prev) => resort(prev.map((it) => (it.id === id ? { ...it, votes: data.votes } : it))));
      const next = new Set(voted);
      next.add(id);
      setVoted(next);
      localStorage.setItem(VOTED_KEY, JSON.stringify([...next]));
    } catch {
      toast.error("Couldn't record your vote");
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.issue.trim()) {
      toast.error("Please fill in every field");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/brands/${brand.slug}/issues`, form);
      setIssues((prev) => resort([{ ...data, votes: 0 }, ...prev]));
      setForm({ name: "", email: "", phone: "", issue: "" });
      toast.success("Thanks — your issue has been posted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="border-t border-hair py-8" data-testid="brand-issues">
      <h2 className="flex items-center gap-2 font-serif text-2xl font-medium text-ink">
        <MessageSquareWarning className="h-5 w-5 text-accent" /> Issues reported by customers
      </h2>
      <p className="mt-2 max-w-2xl text-sm text-muted">
        Facing a problem with {brand.name}? Share it below to warn and help others, and upvote
        the issues you&apos;ve hit too. Only your name is shown publicly — your email and phone
        stay private.
      </p>

      {/* Public list */}
      <div className="mt-6 space-y-px bg-hair" data-testid="brand-issues-list">
        {issues.length === 0 ? (
          <p className="bg-paper p-5 text-sm text-muted" data-testid="brand-issues-empty">
            No issues reported yet. Be the first to share your experience.
          </p>
        ) : (
          issues.map((it) => (
            <div key={it.id} className="flex items-start gap-4 bg-paper p-5" data-testid={`brand-issue-${it.id}`}>
              <button
                type="button"
                onClick={() => upvote(it.id)}
                data-testid={`issue-upvote-${it.id}`}
                aria-label="Upvote this issue"
                className={`flex flex-col items-center gap-0.5 border-2 px-2.5 py-1.5 transition-colors ${
                  voted.has(it.id) ? "border-accent text-accent" : "border-hair text-muted hover:border-accent hover:text-accent"
                }`}
              >
                <ChevronUp className="h-4 w-4" />
                <span className="font-mono text-xs" data-testid={`issue-votes-${it.id}`}>{it.votes || 0}</span>
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted">
                  <User className="h-3.5 w-3.5" />
                  <span className="text-ink">{it.name}</span>
                  <span aria-hidden>·</span>
                  <span>{shortDate(it.created_at)}</span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-ink">{it.issue}</p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Submit form */}
      <form onSubmit={submit} className="mt-8 border border-hair p-5 sm:p-6" data-testid="brand-issue-form">
        <h3 className="font-serif text-lg font-medium text-ink">Report an issue you faced</h3>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <input value={form.name} onChange={set("name")} placeholder="Your name" data-testid="issue-form-name" className={inputCls} />
          <input value={form.email} onChange={set("email")} type="email" placeholder="Email (private)" data-testid="issue-form-email" className={inputCls} />
          <input value={form.phone} onChange={set("phone")} placeholder="Phone (private)" data-testid="issue-form-phone" className={inputCls} />
        </div>
        <textarea value={form.issue} onChange={set("issue")} rows={3} placeholder={`Describe the issue you had with ${brand.name}…`} data-testid="issue-form-issue" className={`mt-3 ${inputCls}`} />
        <button type="submit" disabled={submitting} data-testid="issue-form-submit" className="mt-4 inline-flex items-center gap-2 bg-accent px-6 py-3 font-mono text-sm uppercase tracking-widest text-paper transition-opacity hover:opacity-90 disabled:opacity-50">
          <Send className="h-4 w-4" /> {submitting ? "Posting…" : "Post issue"}
        </button>
      </form>
    </section>
  );
}
